import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { midas } from '@resurs/midas'
import {
  creditTerminalTransaction,
  creditTransaction,
  getCartSummary,
} from '../../../store/actions/transactions-actions'
import { getText } from '../../../internal/TextLabels'
import { setupCartItems } from '../shared/transaction-utils'
import TransactionCartUIStore from './UI/TransactionCartUIStore'
import { format } from '../../../models/format'
import { ActionTypes } from '../../../util/transactionConstants'

const createStateOfTheArticles = ({
  transactionCart,
  action: { loading = false },
}) => {
  const cartItems = (transactionCart || {}).cartItems || []
  if (cartItems.length && !loading) {
    const itemFilter = cartItem =>
      cartItem.debitedQuantity > 0 ||
      cartItem.creditedQuantity > 0 ||
      cartItem.canceledQuantity > 0
    return {
      articles: setupCartItems(cartItems.filter(itemFilter)),
      initialArticles: cartItems,
    }
  }
  return {
    articles: [],
  }
}

const TransactionCartStore = props => {
  const [action, setAction] = useState({
    type: null,
    showHandler: false,
  })

  const [lastProps, setLastProps] = useState(props)
  const [hasActiveErrors, setHasActiveErrors] = useState(false)
  const [errors, setErrors] = useState([])
  const [stateOfTheArticle, setStateOfTheArticle] = useState(
    createStateOfTheArticles(props),
  )
  const [loadingCartSummary, setLoadingCartSummary] = useState(
    !!props.transactionId,
  )
  const [creditTerminalTransactionValue, setCreditTerminalTransactionValue] =
    useState(0)
  const [
    creditTerminalTransactionIsValid,
    setCreditTerminalTransactionIsValid,
  ] = useState(true)

  const isUpdateOnAction = useRef(false)
  const isUpdateErrors = useRef(false)

  const onCancel = () => {
    setAction({
      showHandler: null,
      type: null,
    })
    setErrors([])
  }

  const updateArticles = items => {
    setStateOfTheArticle({
      ...stateOfTheArticle,
      articles: items,
    })
  }

  const updateErrors = () => {
    let newAmountError = false
    let terminalAmountError = false
    if (stateOfTheArticle.articles && stateOfTheArticle.articles.length > 0) {
      newAmountError = !stateOfTheArticle.articles[0].newAmountValid

      setErrors([
        {
          name: 'newAmountError',
          error: newAmountError,
          message: getText(
            'transactions.transaction.specification.newAmountError',
            props.cartStatus.creditableAmount,
          ),
        },
      ])
      setHasActiveErrors(newAmountError)
    }
    if (creditTerminalTransactionValue) {
      terminalAmountError =
        creditTerminalTransactionValue >
          props.terminalTransactions.current.amount ||
        creditTerminalTransactionValue < 1

      setErrors([
        {
          name: 'terminalAmountError',
          error: terminalAmountError,
          message: getText(
            'transactions.transaction.specification.terminalAmountError',
            props.terminalTransactions.current.amount,
          ),
        },
      ])
      setHasActiveErrors(terminalAmountError)
    }
  }

  const setNewAmount = ({ index, value, isValid }) => {
    const updatedItems = [...stateOfTheArticle.articles]
    updatedItems[index].newAmount = value
    updatedItems[index].newAmountValid = isValid

    return updatedItems
  }

  const setDefaultAmountTerminalTransaction = () => {
    setCreditTerminalTransactionValue(props.terminalTransactions.current.amount)
  }

  const updateItemsWithCalculatedValues = () => {
    const updatedItems = stateOfTheArticle.articles.map(item => {
      const updatedItem = item
      updatedItem.newAmount = props.cartStatus.creditableAmount
      updatedItem.newAmountValid = true
      return updatedItem
    })

    setStateOfTheArticle({
      ...stateOfTheArticle,
      articles: updatedItems,
    })
    updateErrors()
  }

  const checkAvailability = () => {
    return props.cartStatus && props.cartStatus.creditableAmount > 0
  }

  const handleNewAmountChange = (newAmount, index) => {
    updateArticles(
      setNewAmount({
        index,
        value: newAmount,
        isValid:
          newAmount > 0 &&
          newAmount <= props.cartStatus.creditableAmount &&
          new RegExp(format.numberWithMax2Decimal.regex).test(newAmount),
      }),
    )
  }

  const handleTerminalTransactionCredit = amount => {
    isUpdateErrors.current = true
    if (amount <= props.terminalTransactions.current.amount && amount > 0) {
      setCreditTerminalTransactionValue(amount)
      setCreditTerminalTransactionIsValid(true)
    } else {
      setCreditTerminalTransactionValue(amount)
      setCreditTerminalTransactionIsValid(false)
    }
  }

  const nothingToUpdate = () => {
    return !(
      stateOfTheArticle.articles.filter(
        item => item.newAmount !== null && item.newAmount > 0,
      ).length || creditTerminalTransactionValue > 0
    )
  }

  const handleAction = () => {
    if (!creditTerminalTransactionValue) {
      props.credit(
        stateOfTheArticle.articles.slice(0, 1),
        props.transactionId,
        '',
      )
    } else {
      props.creditTerminalTransaction(
        creditTerminalTransactionValue,
        props.terminalTransactions.current,
      )
    }
  }

  useEffect(() => {
    if (JSON.stringify(lastProps) === JSON.stringify(props)) {
      return
    }

    if (
      !props.transactionCart &&
      props.terminalTransactions &&
      props.terminalTransactions.current &&
      !props.action.loading &&
      lastProps.terminalTransactions.latestCredit !==
        props.terminalTransactions.latestCredit
    ) {
      props.onBack()
    } else if (
      props.transactionCart &&
      props.transactionCart.cartItems &&
      props.transactionCart.cartItems.length > 0 &&
      !props.action.loading
    ) {
      setStateOfTheArticle(createStateOfTheArticles(props))
      setAction({
        type: null,
        showHandler: false,
      })
    }

    setLastProps(props)
  })

  useEffect(() => {
    if (props.transactionId) {
      props
        .getCartSummary(props.transactionId)
        .then(() => setLoadingCartSummary(false))
        .catch(() => setLoadingCartSummary(false))
    }
  }, [])

  useEffect(() => {
    if (isUpdateOnAction.current) {
      isUpdateOnAction.current = false
      if (action.type === ActionTypes.CREDIT_AMOUNT) {
        updateItemsWithCalculatedValues()
      } else if (action.type === ActionTypes.CREDIT_TRANSACTION_AMOUNT) {
        setDefaultAmountTerminalTransaction()
      }
    }
  }, [action])

  useEffect(() => {
    if (isUpdateErrors.current) {
      isUpdateErrors.current = false
      updateErrors()
    }
  }, [creditTerminalTransactionValue])

  return loadingCartSummary ? (
    <div style={{ margin: '5rem' }} className={midas.loading} />
  ) : (
    <div>
      <TransactionCartUIStore
        actionState={props.action}
        cartStatus={props.cartStatus}
        errors={errors}
        activeErrors={hasActiveErrors}
        cartArticles={stateOfTheArticle.articles}
        orderLimit={props.orderLimit}
        action={action}
        disablePerformButton={nothingToUpdate()}
        checkAvailability={() => checkAvailability()}
        handleNewAmountChange={(e, index) => {
          handleNewAmountChange(e, index)
          updateErrors()
        }}
        setAction={theAction => {
          isUpdateOnAction.current = true
          setAction({
            type: theAction,
            showHandler: true,
          })
        }}
        handleAction={() => handleAction()}
        transactionDetails={props.transactionDetails}
        terminalTransaction={props.terminalTransactions.current}
        handleTerminalTransactionCredit={amount =>
          handleTerminalTransactionCredit(amount)
        }
        creditTerminalTransactionValue={`${creditTerminalTransactionValue}`}
        creditTerminalTransactionIsValid={creditTerminalTransactionIsValid}
        onCancel={() => onCancel()}
      />
    </div>
  )
}

TransactionCartStore.propTypes = {
  getCartSummary: PropTypes.func.isRequired,
  credit: PropTypes.func.isRequired,
  orderLimit: PropTypes.any.isRequired,
  cartStatus: PropTypes.object,
  transactionId: PropTypes.string,
  transactionCart: PropTypes.object, //eslint-disable-line
  action: PropTypes.object,
  transactionDetails: PropTypes.object,
  creditTerminalTransaction: PropTypes.func.isRequired,
  terminalTransactions: PropTypes.object,
  onBack: PropTypes.func.isRequired,
}

TransactionCartStore.defaultProps = {
  transactionId: null,
  transactionCart: null,
  cartStatus: null,
  transactionDetails: null,
  action: { loading: false },
  terminalTransactions: {
    current: null,
    latestCredit: null,
  },
}

const mapStateToProps = state => {
  return {
    transactionId: state.transactions.currentTransactionId,
    transactionCart: state.transactions.currentTransactionCart.cart,
    cartStatus: state.transactions.currentTransactionCart.cartStatus,
    action: state.transactions.action,
    terminalTransactions: state.transactions.terminalTransactions,
  }
}

const mapDispatchToProps = dispatch => ({
  getCartSummary: transactionId => dispatch(getCartSummary(transactionId)),
  credit: (data, transactionId) => {
    dispatch(creditTransaction(data, transactionId, null))
  },
  creditTerminalTransaction: (amount, transaction) => {
    dispatch(creditTerminalTransaction(amount, transaction))
  },
})

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(TransactionCartStore)
